import './index.module.less'
import { encode } from 'querystring'
import {
  Affix,
  Badge,
  Button,
  Card,
  Carousel,
  Drawer,
  message,
  Select,
  Skeleton,
  Space,
} from 'antd'
import { useEffect, useState, useRef } from 'react'
import Router from 'next/router'
import dayjs from 'dayjs'

import { HeartFilled, HeartOutlined } from '@ant-design/icons'
import ViewsCount from '@assets/ViewsCount.svg'
import analytics from '../../lib/analytics'
import { fetcher, useFetch } from '../../lib/hooks'

import InfoCard from '../../components/InfoCard'
import ClassTypeTag from '../../components/ClassTypeTag'
import OhSnap from '../../components/OhSnap'
import DateMenu from '../../components/DateMenu'
import ViewSwitch from '../../components/ScheduleViewSwitch'
import WhatsappConsent from '../../components/WhatsappConsent'

import ClassesPausedSvg from '../../assets/ClassesPaused.svg'
import NoClassesSvg from '../../assets/NoClasses.svg'
import CloseSvg from '../../assets/ClearBlack.svg'
import FilterIcon from '../../assets/FilterIcon.svg'

const createScreen = (title, desc, svg) => () =>
  (
    <InfoCard title={title} description={desc}>
      {svg}
    </InfoCard>
  )

const Paused = createScreen(
  'Classes Paused!',
  'Sorry the content is paused by your principal/AC',
  <ClassesPausedSvg />,
)
const Holiday = createScreen(
  'Holiday!',
  'Time for a break. Enjoy your free day!',
  <NoClassesSvg />, // same svg as no-class
)
const NoClass = createScreen(
  'No Classes Today!',
  'You don’t have a class scheduled today.',
  <NoClassesSvg />,
)

const { Option } = Select
const EOD = dayjs().endOf('day')

const MASTERCLASS_DAYS =
  dayjs().isAfter(process.env.NEXT_PUBLIC_EVENT_STARTDATE) &&
  dayjs().isBefore(process.env.NEXT_PUBLIC_EVENT_ENDDATE)

export default function Home(props) {
  const {
    date: datestr,
    type = '0',
    class: classKey = '0-0-0',
    subject = '0',
  } = props
  const date = datestr ? dayjs(datestr) : dayjs()
  const [selectType, setSelectType] = useState('')
  const [dateFilter, setDateFilter] = useState()
  const [isMasp] = useState(
    () =>
      typeof window !== 'undefined' &&
      JSON.parse(localStorage.getItem('masp'))?.isMasp,
  )
  const [hideClickClassHelp, setHideClickClassHelp] = useState(true)
  const allSubject = { 0: 'All Subjects' }
  const [classes, setClasses] = useState({
    '0-0-0': { name: 'All Classes', subjects: { ...allSubject } },
  })
  const [whatsappConsentInfo, setWhatsappConsentInfo] = useState({
    show: false,
  })
  const { user = {} } =
    typeof window !== 'undefined'
      ? JSON.parse(localStorage.getItem('auth') || '{}')
      : {}
  const consents =
    typeof window !== 'undefined'
      ? JSON.parse(localStorage.getItem(`consents_${user.id}`))
      : []
  const [feedsOfTheDay, setFeedsOfTheDay] = useState([])

  const classTypes = {
    0: 'Class Type',
    '1,2,3': '@Home',
    4: '@School',
  }

  const currentLikedSlide = useRef(0)

  const thoughtGreetingsEvent = (name, index, action = 'liked') => {
    const { su: [su] = [{}] } = user
    analytics.track(`${name}`, {
      teacher_id: su.id,
      school_id: su.schoolid,
      school_name: su.schoolname,
      role: su.rolename,
      [`${action === 'liked' ? 'no_of_likes' : 'no_of_views'}`]:
        action === 'viewed'
          ? feedsOfTheDay[index].views_count + 1
          : feedsOfTheDay[index].likes_count + 1,
    })
  }

  const updateFeedView = (currentFeed) => {
    const curObject = feedsOfTheDay[currentFeed]
    const opts = {
      body: {
        feed_id: curObject._id,
      },
    }
    if (!curObject.current_user_viewed)
      fetcher(`/feeds/view`, opts)
        .then(({ success }) => {
          if (success) {
            const updatedFeeds = feedsOfTheDay.map((item) => {
              if (item._id === curObject._id)
                return {
                  ...item,
                  current_user_viewed: true,
                  views_count: item.views_count + 1,
                }
              return item
            })
            currentLikedSlide.current = currentFeed
            if (curObject.feed_type === 'greeting') {
              thoughtGreetingsEvent('greetings_viewed', currentFeed, 'viewed')
            } else {
              thoughtGreetingsEvent(
                'thought_of_the_day_viewed',
                currentFeed,
                'viewed',
              )
            }
            setFeedsOfTheDay(updatedFeeds)
          }
        })
        .catch((err) =>
          message.error({
            content: err.message,
            className: 'lead-toast',
          }),
        )
  }
  useEffect(() => {
    if (!localStorage.getItem(`hide_click_class_help_${user.id}`))
      setHideClickClassHelp(false)

    if (user.id) {
      fetcher('/classSubjects').then((data) => {
        const filters = { ...classes }
        ;(data.classsubjects || []) // TODO: class sorting by LMS
          .forEach(
            ({ classId, className, divisions, academicYearId: ay = '0' }) => {
              divisions.forEach(({ divisionName, divisionId, subjects }) => {
                filters[`${classId}-${divisionId}-${ay}`] = {
                  name: `${className} - ${divisionName}`,
                  subjects: subjects.reduce((z, s) => {
                    filters['0-0-0'].subjects[s.subjectId] = s.subjectName // mutation inside reduce
                    return { ...z, [s.subjectId]: s.subjectName }
                  }, allSubject),
                }
              })
            },
          )

        setClasses(filters)
      })
      fetcher(`/feeds`).then(({ success, data }) => {
        if (success) {
          setFeedsOfTheDay(data)
        }
      })
    }
  }, [])

  useEffect(() => {
    // call the popup template api if whatsapp consent is not accepted
    if (!consents || consents.indexOf('WHATSAPP') === -1) {
      fetcher('/popup').then(
        ({
          consents: consentData,
          templates: {
            popup_template_id: templateId,
            _id: docId,
            category,
            close_action: closeAction,
            cta_action: ctaAction,
          },
        }) => {
          localStorage.setItem(
            `consents_${user.id}`,
            JSON.stringify(consentData || []),
          )
          if (templateId && category === 'WHATSAPP') {
            setWhatsappConsentInfo({
              show: true,
              docId,
              closeData: closeAction.data,
              acceptData: ctaAction.data,
            })
          }
        },
      )
    }
  }, [])

  const consentAction = (action) => {
    fetcher(`/popup/consent/${whatsappConsentInfo.docId}`, {
      body: {
        data:
          action === 'CLOSE'
            ? whatsappConsentInfo.closeData
            : whatsappConsentInfo.acceptData,
      },
    })
      .then(
        () => {
          setWhatsappConsentInfo({ show: false })
          if (action === 'SAVE') {
            localStorage.setItem(
              `consents_${user.id}`,
              JSON.stringify([...consents, 'WHATSAPP']),
            )
          }
        },
        (err) => {
          setWhatsappConsentInfo({ show: false })
          message.error({
            content: err.message,
            className: 'lead-toast',
          })
        },
      )
      .finally(() => {
        if (action === 'CLOSE') {
          analytics.track('whatsapp_consent_cancelled', {
            teacher_id: user.id,
            role: user.su[0].rolename,
            teacher_name: user.name,
          })
        } else {
          analytics.track('whatsapp_consent_provided', {
            teacher_id: user.id,
            role: user.su[0].rolename,
            teacher_name: user.name,
          })
        }
      })
  }

  const scrollHandler = () => {
    const page = document?.getElementById('content-main')
    if (
      page?.offsetHeight === page?.scrollHeight ||
      page?.scrollTop + page?.offsetHeight >= page?.scrollHeight
    ) {
      updateFeedView(0)
    }
  }

  useEffect(() => {
    const page = document?.getElementById('content-main')
    if (feedsOfTheDay.length) {
      if (page?.offsetHeight === page?.scrollHeight) {
        // raise viewed event
        updateFeedView(0)
      } else {
        // add event listener
        page.addEventListener('scroll', scrollHandler)
        if (page?.scrollTop + page?.offsetHeight >= page?.scrollHeight) {
          // check current scroll position, if it is already at the bottom
          updateFeedView(0)
        }
      }
    }
    return () => page.removeEventListener('scroll', scrollHandler)
  }, [feedsOfTheDay])

  const setQuery = (value) => {
    if (value.date) {
      analytics.track('my_classes_date_selected', {
        selected_date: value.date,
        interval: -EOD.diff(value.date, 'day'),
      })
    } else {
      let filterBy = 'Subject'
      let filterValue = classes[classKey]?.subjects?.[value.subject]
      if ('class' in value) {
        filterBy = 'Class'
        filterValue = classes[value.class].name
      }

      if ('type' in value) {
        filterBy = 'Class Type'
        filterValue = classTypes[value.type]
      }

      analytics.track('my_classes_filter_selected', {
        filter_by: filterBy,
        filter_value: filterValue,
      })
    }

    Router.push(
      `/classes?${encode({
        ...Router.query,
        ...value,
      })}`,
    )
  }

  const setSubject = (value) => setQuery({ subject: value })

  const setClass = (key) => {
    setQuery({
      class: key,
      ...(classes[key].subjects[subject] ? {} : { subject: '0' }),
    })
  }

  const setClassType = (value) => setQuery({ type: value })

  const [classId, division, ay] = classKey.split('-')
  const filters = [
    ...Object.entries({ type, class: classId, division, subject }),
  ]
    .filter(([, v]) => Number(v.replace(/,/g, '')))
    .reduce((z, [k, v]) => ({ ...z, [k]: v }), {})

  const clsdate = date.format('YYYY-MM-DD')
  const [data, { loading, error }] = useFetch('/calendar/onlineClasses', {
    params: { ...filters, date: clsdate },
    ay,
  })

  const MainFilter = () => (
    <div>
      <Affix target={() => document.getElementById('content-main')}>
        <DateMenu
          date={clsdate}
          view={dateFilter}
          onViewChange={setDateFilter}
          onSelect={(d) => setQuery({ date: d })}
        />

        {/* Banner */}
        {!hideClickClassHelp && !!((data || {}).onlineClasses || []).length && (
          <div className="click-class-help">
            <span>Click on class cards below for more details.</span>
            <Button
              type="link"
              size="large"
              className="click-class-help-close"
              onClick={() => {
                localStorage.setItem(`hide_click_class_help_${user.id}`, true)
                setHideClickClassHelp(true)
              }}
              icon={<CloseSvg />}
            />
          </div>
        )}
      </Affix>
      {MASTERCLASS_DAYS && process.env.NEXT_PUBLIC_EVENT_BANNER && (
        <a
          href={process.env.NEXT_PUBLIC_EVENT_REGION_BASED_SCHOOLS?.includes(user?.su?.[0]?.schoolid) ? process.env.NEXT_PUBLIC_EVENT_REGION_BASED_LINK : process.env.NEXT_PUBLIC_EVENT_LINK}
          target="_blank"
          rel="noreferrer"
          onClick={() => analytics.track('banner_clicked')}
        >
          <img
            src={process.env.NEXT_PUBLIC_EVENT_BANNER}
            alt="Event Session"
            width="100%"
          />
        </a>
      )}
      <div className="timetable-filter">
        {!isMasp && (
          <ViewSwitch
            options={[
              ['My Classes', ''],
              ['Assessments', '/assessments'],
            ]}
            onChange={(e) => {
              Router.push(`/classes${e.target.value}?${encode(Router.query)}`)
            }}
          />
        )}
        <Button
          className={isMasp && 'filter-right'}
          type="text"
          shape="circle"
          icon={
            <Badge dot={Object.keys(filters).length > 0}>
              <FilterIcon />
            </Badge>
          }
          onClick={() => setSelectType('class')}
        />
      </div>
      <Drawer
        height="auto"
        className="drawer-filter"
        placement="bottom"
        title="Apply Filters"
        visible={!!selectType}
        onClose={() => setSelectType('')}
        closeIcon={<CloseSvg fill="black" />}
        destroyOnClose
      >
        <Space className="class-subject-filter" align="center">
          <Select size="large" value={type} onSelect={setClassType}>
            {[...Object.entries(classTypes)].map(([key, name]) => (
              <Option value={key} key={key}>
                {name}
              </Option>
            ))}
          </Select>

          <Select
            size="large"
            value={classes[classKey] ? classKey : ''}
            onSelect={setClass}
          >
            {[...Object.entries(classes)].map(([key, { name }]) => (
              <Option value={key} key={key}>
                {name}
              </Option>
            ))}
          </Select>

          <Select
            size="large"
            value={classes['0-0-0'].subjects[subject] ? subject : ''}
            onSelect={setSubject}
          >
            <Option value="0" key="0">
              {allSubject[0]}
            </Option>
            {[...Object.entries((classes[classKey] || {}).subjects || [])]
              .slice(1)
              .sort(([, a], [, b]) => {
                if (a > b) return 1
                if (b > a) return -1
                return 0
              })
              .map(([key, name]) => (
                <Option value={key} key={key}>
                  {name}
                </Option>
              ))}
          </Select>
        </Space>
        <Button block type="link" onClick={() => setSelectType('')}>
          Cancel
        </Button>
      </Drawer>
    </div>
  )
  const likeFeed = ({ _id, current_user_liked: liked }, currentFeed) => {
    if (liked) {
      message.info({
        content: 'Feed already liked',
        className: 'lead-toast',
      })
      return
    }
    const opts = {
      body: {
        feed_id: _id,
      },
    }
    fetcher(`/feeds/like`, opts)
      .then(({ success }) => {
        if (success) {
          const updatedFeeds = feedsOfTheDay.map((item) => {
            if (item._id === _id)
              return {
                ...item,
                current_user_liked: true,
                likes_count: item.likes_count + 1,
              }
            return item
          })
          currentLikedSlide.current = currentFeed
          if (feedsOfTheDay[currentFeed].feed_type === 'greeting') {
            thoughtGreetingsEvent('greetings_liked', currentFeed)
          } else {
            thoughtGreetingsEvent('thought_of_the_day_liked', currentFeed)
          }
          setFeedsOfTheDay(updatedFeeds)
        }
      })
      .catch((err) =>
        message.error({
          content: err.message,
          className: 'lead-toast',
        }),
      )
  }

  const FeedOfTheDay = ({ item, currentFeed }) => (
    <div className="carousel-container">
      <Card
        className="carousel-card"
        hoverable
        cover={
          <>
            <img alt={item.feed_type} src={item.feed_media_url} />
          </>
        }
      >
        <div className="additional-info">
          <div className="view-like-countSection">
            <ViewsCount />
            <p>
              {item.views_count} {item.views_count === 1 ? 'View' : 'Views'}
            </p>
          </div>
          <div
            className="like-status-button"
            onClick={() => likeFeed(item, currentFeed)}
            aria-hidden="true"
          >
            {item.current_user_liked ? (
              <>
                <HeartFilled style={{ color: '#f2463b' }} />
                <p>
                  {item.likes_count} {item.likes_count === 1 ? 'Like' : 'Likes'}
                </p>
              </>
            ) : (
              <>
                <HeartOutlined />
                <p>
                  {item.likes_count} {item.likes_count === 1 ? 'Like' : 'Likes'}
                </p>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  )
  const MyClasses = ({ body }) => (
    <>
      <MainFilter />
      {body}
      {whatsappConsentInfo.show && (
        <WhatsappConsent
          schoolName={user.su[0].schoolname}
          consentAction={consentAction}
        />
      )}
      {!!feedsOfTheDay.length && (
        <div className="feeds-carousal-container">
          <h3 className="heading-thoughtOfTheDay">Thoughts & Greetings</h3>
          <Carousel
            afterChange={(cur) => updateFeedView(cur)}
            currentSlide={currentLikedSlide.current}
          >
            {feedsOfTheDay.map((item, index) => (
              <div key={item._id}>
                {item && <FeedOfTheDay item={item} currentFeed={index} />}
              </div>
            ))}
          </Carousel>
        </div>
      )}
    </>
  )

  if (error) return <MyClasses body={<OhSnap />} />

  const Loader = () => (
    <div className="timeline">
      {[1, 2, 1].map((length, key) => (
        <div className="period" key={key}>
          <time>
            <Skeleton.Button active round size="small" />
          </time>
          <div className="classes">
            {Array.from({ length }).map((_, innerKey) => (
              <Card
                size="small"
                className="class"
                bordered={false}
                key={innerKey}
              >
                <Skeleton
                  active
                  title={false}
                  paragraph={{ rows: 2, width: ['80%', '100px'] }}
                  size="small"
                  round
                />
                <div
                  style={{
                    position: 'absolute',
                    right: '-10px',
                    bottom: '8px',
                  }}
                >
                  <Skeleton.Button
                    size="small"
                    style={{ width: 100 }}
                    active
                    round
                  />
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  )

  if (loading) return <MyClasses body={<Loader />} />
  if (data.paused) return <MyClasses body={<Paused />} />
  if (data.holiday) return <MyClasses body={<Holiday />} />

  const NO_CLASS = !(data.onlineClasses || []).length
  if (NO_CLASS) return <MyClasses body={<NoClass />} />

  const toClass = (cls, classdate, canEdit) => {
    const { mid, classtype } = cls

    analytics.track('class_card_clicked', {
      class_scheduled_date: classdate,
      class_card_type:
        ['@School Class', 'Lead School@Home'][cls.type % 4] || '@Home Class',
      content_type: classtype.endsWith(' Live') ? 'Live' : 'Recorded',

      class_topic: cls.title, // TODO
      class: cls.className,
      div: cls.divisionName,
      subject: cls.subjectName,
      is_subject_teacher: cls.is_subject_teacher,
      subject_type: cls.type === 1 ? 'Lead' : 'Non-Lead',
      teacher_edit_permission: canEdit,

      unit: cls.unitNo,
      unit_day: cls.dayNo,
      is_revision: cls.revisionDay,

      // TODO: is_assessment, is_homework
    })

    const query = encode({
      type: cls.type,
      date: classdate,
      cp: cls.classPublisherId || cls.classpublisherid,
      div: cls.divisionId,
      ay: cls.academicYearId,
      ...(cls.revisionDay ? { revision: 1 } : {}),
    })

    switch (cls.type) {
      case 1:
        Router.push('/classes/[id]', `/classes/${mid}?${query}`)
        break
      case 4:
        Router.push('/classes/[id]/school', `/classes/${mid}/school`)
        break
      default:
        Router.push('/classes/[id]/myclass', `/classes/${mid}/myclass?${query}`)
    }
  }

  const toTime = (time) => {
    const [hr, mm] = time.split(':')
    return `${`${hr % 12 || 12}`.padStart(2, 0)}:${mm} ${hr >= 12 ? 'P' : 'A'}M`
  }

  const timeline = () =>
    [
      ...Object.entries(
        (data.onlineClasses || []).reduce((acc, cls) => {
          const hr = cls.classStartTime.slice(0, 5).padStart(5, 0)
          if (!acc[hr]) acc[hr] = []
          acc[hr].push(cls)
          return acc
        }, {}),
      ),
    ].sort(([k], [kn]) => -(k < kn) || +(k > kn))

  const ClassList = () => (
    <div className="timeline">
      {timeline().map(([hr, classList]) => (
        <div className="period" key={hr}>
          <time>{toTime(hr)}</time>
          <div className="classes">
            {classList.map((cls) => (
              <Card
                className="class"
                title={cls.title}
                extra={
                  cls.classtype.endsWith(' Live') && (
                    <div className="live-icon">Live</div>
                  )
                }
                bordered={false}
                size="small"
                key={`${
                  cls.mid || cls.classPublisherId || cls.classpublisherid
                }`}
                onClick={() => {
                  toClass(cls, `${clsdate} ${hr}`, data.userEditAccess)
                }}
                bodyStyle={{ padding: '0 12px 12px' }}
              >
                <Card.Meta
                  description={`${cls.className} - ${cls.divisionName}`}
                />
                {!cls.mid &&
                  data.userEditAccess &&
                  date.isAfter(EOD) &&
                  [2, 3].includes(cls.type) && (
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#F05757',
                        fontWeight: 500,
                        marginTop: '8px',
                      }}
                    >
                      Add learning material
                    </div>
                  )}
                <ClassTypeTag type={cls.type} />
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  )

  return <MyClasses body={<ClassList />} />
}

export function getServerSideProps({ query }) {
  return {
    props: {
      ...query,
      title: 'Timetable',
    },
  }
}
